import React, { useState, useMemo } from "react";
import { ButtonComp, CardComp } from "../../../../components";
import {
  useGetCompanyLoanTypesQuery,
  useImportLoanTypesMutation,
} from "../../../../store/admin/loan";
import toast from "react-hot-toast";
import { formatMoney } from "../../../../utils";
import TableSkeletonLoader from "../../../../components/Common/TableSkeletonLoader";

const ImportLoanTypesModal = ({ handleClose }) => {
  const [selectedLoanTypes, setSelectedLoanTypes] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading, isFetching } = useGetCompanyLoanTypesQuery();
  const [importLoanTypes, { isLoading: isImporting }] =
    useImportLoanTypesMutation();

  const companyLoanTypes = data || [];

  // Filter loan types by search term
  const filteredLoanTypes = useMemo(() => {
    if (!searchTerm) return companyLoanTypes;
    return companyLoanTypes.filter((loanType) =>
      loanType?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [companyLoanTypes, searchTerm]);

  const handleToggleSelection = (loanTypeId) => {
    setSelectedLoanTypes((prev) => {
      if (prev.includes(loanTypeId)) {
        return prev.filter((id) => id !== loanTypeId);
      }
      return [...prev, loanTypeId];
    });
  };

  const handleSelectAll = () => {
    if (selectedLoanTypes.length === filteredLoanTypes.length) {
      setSelectedLoanTypes([]);
    } else {
      setSelectedLoanTypes(filteredLoanTypes.map((lt) => lt.id));
    }
  };

  const handleImport = async () => {
    if (selectedLoanTypes.length === 0) {
      return toast.error("Please select at least one loan type to import");
    }

    const response = await importLoanTypes({
      loan_type_ids: selectedLoanTypes,
    });

    const { data, error } = response;
    if (error) {
      return toast.error(error?.data?.message || "An error occurred");
    } else if (data?.status === false) {
      return toast.error(data?.message || "An error occurred");
    } else {
      toast.success(
        data?.message ||
          `Successfully imported ${selectedLoanTypes.length} loan type(s)`
      );
      handleClose();
    }
  };

  const isAllSelected =
    filteredLoanTypes.length > 0 &&
    selectedLoanTypes.length === filteredLoanTypes.length;

  return (
    <div className="py-3">
      <div className="mb-4">
        <h5 className="fw-bold mb-2 text-black1">Import Loan Types</h5>
        <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
          Select loan types from other branches in your company to import into
          your current branch
        </p>
      </div>

      {/* Search Input */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search loan types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            padding: "10px 15px",
            borderRadius: "5px",
            border: "1px solid #a1a0a0",
            fontSize: "14px",
          }}
        />
      </div>

      {/* Loan Types List */}
      <CardComp
        cardClassName="border-0 rounded-10 px-0 mb-4 shadow3"
        cardBodyClassName="p-0"
      >
        {isLoading || isFetching ? (
          <div className="p-4">
            <TableSkeletonLoader rows={5} columns={1} />
          </div>
        ) : filteredLoanTypes.length === 0 ? (
          <div className="p-4 text-center text-muted">
            {searchTerm
              ? "No loan types found matching your search"
              : "No loan types available from other branches"}
          </div>
        ) : (
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {/* Select All Checkbox */}
            <div
              className="px-4 py-3 border-bottom"
              style={{
                backgroundColor: "#f7f7f7",
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="selectAll"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  style={{ cursor: "pointer" }}
                />
                <label
                  className="form-check-label fw-bold text-black1"
                  htmlFor="selectAll"
                  style={{ cursor: "pointer", fontSize: "14px" }}
                >
                  Select All ({filteredLoanTypes.length})
                </label>
              </div>
            </div>

            {/* Loan Types List */}
            {filteredLoanTypes.map((loanType) => {
              const isSelected = selectedLoanTypes.includes(loanType.id);
              return (
                <div
                  key={loanType.id}
                  className={`px-4 py-3 border-bottom ${
                    isSelected ? "bg-light" : ""
                  }`}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                  onClick={() => handleToggleSelection(loanType.id)}
                >
                  <div className="d-flex align-items-center">
                    <div className="form-check me-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelection(loanType.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="fw-bold text-black1 mb-0" style={{ fontSize: "14px" }}>
                          {loanType.name || "N/A"}
                        </h6>
                        <span
                          className="badge rounded-pill"
                          style={{
                            backgroundColor: "var(--color-secondary)",
                            color: "white",
                            fontSize: "12px",
                            padding: "4px 10px",
                          }}
                        >
                          {loanType.rate || 0}%
                        </span>
                      </div>
                      <div className="d-flex flex-wrap gap-3" style={{ fontSize: "12px" }}>
                        <span className="text-muted">
                          Guarantors: {loanType.min_guarantor || 0} -{" "}
                          {loanType.max_guarantor || 0}
                        </span>
                        <span className="text-muted">
                          Amount: {formatMoney(loanType.min_loan_amount || 0)} -{" "}
                          {formatMoney(loanType.max_loan_amount || 0)}
                        </span>
                        {loanType.branch_name && (
                          <span className="text-muted">
                            Branch: {loanType.branch_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardComp>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-3">
        <ButtonComp
          btnText="Cancel"
          onClick={handleClose}
          btnClassName="bg-white py-2 px-4 rounded text-black1 border-2"
          style={{
            minWidth: "120px",
            borderColor: "var(--color-primary)",
            color: "var(--color-primary)",
          }}
        />
        <ButtonComp
          btnText={
            isImporting
              ? "Importing..."
              : `Import ${selectedLoanTypes.length > 0 ? `(${selectedLoanTypes.length})` : ""}`
          }
          onClick={handleImport}
          btnClassName="bg-primary1 py-2 px-4 rounded text-white border-0"
          style={{ minWidth: "150px" }}
          loading={isImporting}
          disabled={selectedLoanTypes.length === 0 || isImporting}
        />
      </div>
    </div>
  );
};

export default ImportLoanTypesModal;

